import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { msalInstance } from '@/auth/msalConfig';
import RequireAuth from '@/auth/RequireAuth';
import Layout from '@/components/Layout';

// ── Lazy-loaded pages ────────────────────────────────────────────────────────
const HomePage = React.lazy(() => import('@/pages/HomePage'));
const MeetingIntelligence = React.lazy(() => import('@/pages/MeetingIntelligence'));
const AdminPage = React.lazy(() => import('@/pages/AdminPage'));
const StrategyPage = React.lazy(() => import('@/pages/StrategyPage'));
const AboutPage = React.lazy(() => import('@/pages/AboutPage'));
const AboutTeeMeePage = React.lazy(() => import('@/pages/AboutTeeMeePage'));
const TownHallsPage = React.lazy(() => import('@/pages/TownHallsPage'));
const ExecutiveTeamPage = React.lazy(() => import('@/pages/ExecutiveTeamPage'));
const CEOMessagePage = React.lazy(() => import('@/pages/CEOMessagePage'));
const AskAndrewPage = React.lazy(() => import('@/pages/AskAndrewPage'));
const KPITrackerPage = React.lazy(() => import('@/pages/KPITrackerPage'));
const CompanyKPIsPage = React.lazy(() => import('@/pages/CompanyKPIsPage'));
const KPIDetailPage = React.lazy(() => import('@/pages/KPIDetailPage'));
const DocumentLibraryPage = React.lazy(() => import('@/pages/DocumentLibraryPage'));
const AnalyticsReportPage = React.lazy(() => import('@/pages/AnalyticsReportPage'));
const SalesOverviewPage = React.lazy(() => import('@/pages/SalesOverviewPage'));
const TechProgressionPage = React.lazy(() => import('@/pages/TechProgressionPage'));
const TechProgressionDashboardPage = React.lazy(() => import('@/pages/TechProgressionDashboardPage'));
const TechProgressionReviewPage = React.lazy(() => import('@/pages/TechProgressionReviewPage'));
const SixBoxPage = React.lazy(() => import('@/pages/SixBoxPage'));
const HighlightNominatePage = React.lazy(() => import('@/pages/HighlightNominatePage'));
const IntakeRoutes = React.lazy(() => import('@/pages/intake/IntakeRoutes'));

// ── Page loading fallback ────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-6 h-6 border-2 border-jm-charcoal/20 border-t-jm-charcoal rounded-full animate-spin" />
    </div>
  );
}

// ── TanStack Query client ────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof Error) {
          const msg = error.message.toLowerCase();
          if (
            msg.includes('not found') ||
            msg.includes('permission') ||
            msg.includes('session has expired')
          ) {
            return false;
          }
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// ── Authenticated layout wrapper ──────────────────────────────────────────────
function AuthenticatedLayout({ children, requiredRole, requiredRoles }: { children: React.ReactNode; requiredRole?: string; requiredRoles?: string[] }) {
  return (
    <RequireAuth requiredRole={requiredRole} requiredRoles={requiredRoles}>
      <Layout>
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </Layout>
    </RequireAuth>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Home — employee dashboard */}
            <Route
              path="/"
              element={
                <AuthenticatedLayout>
                  <HomePage />
                </AuthenticatedLayout>
              }
            />

            {/* Portal pages */}
            <Route
              path="/strategy"
              element={
                <AuthenticatedLayout>
                  <StrategyPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/about"
              element={
                <AuthenticatedLayout>
                  <AboutPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/about/teemee"
              element={
                <AuthenticatedLayout>
                  <AboutTeeMeePage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/town-halls"
              element={
                <AuthenticatedLayout>
                  <TownHallsPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/executives"
              element={
                <AuthenticatedLayout>
                  <ExecutiveTeamPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/ceo-message"
              element={
                <AuthenticatedLayout>
                  <CEOMessagePage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/ask-andrew"
              element={
                <AuthenticatedLayout>
                  <AskAndrewPage />
                </AuthenticatedLayout>
              }
            />

            {/* KPI Tracker */}
            <Route
              path="/kpis"
              element={
                <AuthenticatedLayout>
                  <KPITrackerPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/kpis/company"
              element={
                <AuthenticatedLayout>
                  <CompanyKPIsPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/kpis/:id"
              element={
                <AuthenticatedLayout>
                  <KPIDetailPage />
                </AuthenticatedLayout>
              }
            />

            {/* Documents */}
            <Route
              path="/documents"
              element={
                <AuthenticatedLayout>
                  <DocumentLibraryPage />
                </AuthenticatedLayout>
              }
            />

            {/* 1:1 Intelligence */}
            <Route
              path="/people"
              element={
                <AuthenticatedLayout>
                  <MeetingIntelligence />
                </AuthenticatedLayout>
              }
            />

            {/* Employee Highlight nomination form (Carolyn 2026-05-07, E5) */}
            <Route
              path="/highlights/nominate"
              element={
                <AuthenticatedLayout>
                  <HighlightNominatePage />
                </AuthenticatedLayout>
              }
            />

            {/* Admin — admin role only */}
            <Route
              path="/admin"
              element={
                <AuthenticatedLayout requiredRole="Atlas.Admin">
                  <AdminPage />
                </AuthenticatedLayout>
              }
            />

            {/* Sales Overview (internal analytics dashboard) */}
            <Route
              path="/analytics/sales-overview"
              element={
                <AuthenticatedLayout>
                  <SalesOverviewPage />
                </AuthenticatedLayout>
              }
            />

            {/* Analytics */}
            <Route
              path="/analytics"
              element={
                <AuthenticatedLayout>
                  <AnalyticsReportPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/analytics/:reportId"
              element={
                <AuthenticatedLayout>
                  <AnalyticsReportPage />
                </AuthenticatedLayout>
              }
            />

            {/* Tech Progression — manager form */}
            <Route
              path="/tech-progression"
              element={
                <AuthenticatedLayout requiredRoles={['Tech_Progression', 'Atlas.Admin']}>
                  <TechProgressionPage />
                </AuthenticatedLayout>
              }
            />

            {/* Tech Progression — admin dashboard. Open to HR-tier
                (HR / HRBP / Executive / Admin) per the spec's access matrix.
                Previously Atlas.Admin-only, which contradicted the nav config
                in config/navigation.ts (TECH_PROGRESSION_ADMIN) and blocked
                HR Director Lanie from her primary entry point on 2026-05-20.
                Write-back endpoints on the API remain Admin-only. */}
            <Route
              path="/tech-progression/admin"
              element={
                <AuthenticatedLayout requiredRoles={['Atlas.HR', 'Atlas.HRBP', 'Atlas.Executive', 'Atlas.Admin']}>
                  <TechProgressionDashboardPage />
                </AuthenticatedLayout>
              }
            />
            <Route
              path="/tech-progression/admin/:id"
              element={
                <AuthenticatedLayout requiredRoles={['Atlas.HR', 'Atlas.HRBP', 'Atlas.Executive', 'Atlas.Admin']}>
                  <TechProgressionDashboardPage />
                </AuthenticatedLayout>
              }
            />

            {/* Tech Progression — V2 routed-workflow review page.
                Open to HR-tier, Tech-tier, Board (Admin), and the Tech_Progression
                role used by managers today; service- and route-layer guards
                enforce who can edit which section. */}
            <Route
              path="/tech-progression/review/:id"
              element={
                <AuthenticatedLayout
                  requiredRoles={[
                    'Atlas.HR',
                    'Atlas.HRBP',
                    'Atlas.Executive',
                    'Atlas.Admin',
                    'Tech_Progression',
                  ]}
                >
                  <TechProgressionReviewPage />
                </AuthenticatedLayout>
              }
            />

            {/* 6-Box Talent — pilot allowlist + Atlas.SixBox role */}
            <Route
              path="/six-box"
              element={
                <AuthenticatedLayout>
                  <SixBoxPage />
                </AuthenticatedLayout>
              }
            />

            {/* Product intake. Its pages sit behind its own gate, in IntakeRoutes (ATLAS-100). */}
            <Route
              path="/intake/*"
              element={
                <AuthenticatedLayout>
                  <IntakeRoutes />
                </AuthenticatedLayout>
              }
            />

            {/* Catch-all — redirect to home */}
            <Route
              path="*"
              element={
                <AuthenticatedLayout>
                  <HomePage />
                </AuthenticatedLayout>
              }
            />
          </Routes>
        </BrowserRouter>

        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </MsalProvider>
  );
}
